/* eslint-disable id-length */

import Icon from "~/components/shared/Icon";
import classNames from "classnames";
import React, { useCallback, useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";
// TODO: replace with react-hotkeys-hook
import { useHotKey } from "~/components/Playground/Controls/useHotKey";
import {
  DockPosition,
  useBuildPreferencesStore,
} from "~/components/build/Show/lib/useBuildPreferencesStore";
import { ResizeHandle } from "~/components/build/Show/components/ResizeHandle";
import { useMobileBreakpoint } from "~/components/build/Show/lib/useBreakpoints";
import ToolbarButton, {
  ToolbarButtonGroup,
} from "~/components/build/Show/components/ToolbarButton";
import debounce from "lodash/debounce";
import { useTrack } from "~/components/build/Show/lib/useTrack";
import { useAdjustedDockPosition } from "~/components/build/Show/lib/useAdjustedDockPosition";

interface Props {
  children: React.ReactNode;
  onClose: () => void;
  actions?: React.ReactNode;
}

const RESIZE_DEBOUNCE_WAIT = 150;

/**
 * A collapsible drawer
 */
export const Drawer = ({ children, actions, onClose }: Props) => {
  const isMobile = useMobileBreakpoint();

  // Get the current dock position from the store, or default to full screen on mobile
  const dockPosition = useBuildPreferencesStore((state) =>
    isMobile ? DockPosition.Full : state.dockPosition,
  );

  const [resetDrawerWidth, resetDrawerHeight, setResizedDrawerHeight, setResizedDrawerWidth] =
    useBuildPreferencesStore((state) => [
      state.resetDrawerWidth,
      state.resetDrawerHeight,
      state.setResizedDrawerHeight,
      state.setResizedDrawerWidth,
    ]);

  const drawerRef = useRef<HTMLDivElement>(null);

  // The size is stored in a ref to avoid re-rendering the component on every resize drag.
  // We subscribe to the store to keep the ref in sync for when the component re-renders
  // (e.g. after switching dock positions and switching back).
  const heightRef = useRef(useBuildPreferencesStore.getState().resizedDrawerHeight);
  const widthRef = useRef(useBuildPreferencesStore.getState().resizedDrawerWidth);

  useEffect(() => {
    return useBuildPreferencesStore.subscribe((state) => {
      heightRef.current = state.resizedDrawerHeight;
      widthRef.current = state.resizedDrawerWidth;
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setWidth = useCallback(
    debounce((width) => setResizedDrawerWidth(width), RESIZE_DEBOUNCE_WAIT),
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setHeight = useCallback(
    debounce((height) => setResizedDrawerHeight(height), RESIZE_DEBOUNCE_WAIT),
    [],
  );

  useHotKey("Escape", () => {
    onClose();
  });

  // Store a reference to the container element
  const containerRef = useRef<HTMLElement>();
  useEffect(() => {
    const container = document.getElementById("drawer-container");
    if (container) {
      containerRef.current = container;
    }
  }, []);

  // TODO: Work out where this comes from
  const margin = 15;

  /**
   * Resize the drawer when dragging the resize handle
   *
   * Hook into the mouse down event to start dragging the resize handle
   * and update the drawer size based on the mouse position, storing the
   * new size in a preferences store for persistence across sessions.
   *
   * Sizes are stored as percentages to allow for responsive resizing.
   */
  const resize = useCallback(
    (x: number, y: number) => {
      if (!drawerRef.current) {
        return;
      }

      // Prevent resizing when the drawer is expanded
      if (dockPosition === DockPosition.Full) {
        return;
      }

      // If the drawer is docked to bottom, resize the height (as a percentage)
      if (dockPosition === DockPosition.Bottom) {
        if (!containerRef.current?.clientHeight) {
          return resetDrawerHeight();
        }

        let height =
          ((window.innerHeight - y - margin) / containerRef?.current?.clientHeight) * 100;

        if (height > 100) {
          height = 100;
        } else if (height < 5) {
          height = 5;
        }

        drawerRef.current.style.height = `${height}%`;
        setHeight(height);
      }

      // If the drawer is docked to right, resize the width (as a percentage)
      if (dockPosition === DockPosition.Right) {
        if (!containerRef.current?.clientWidth) {
          return resetDrawerWidth();
        }

        const containerRect = containerRef.current.getBoundingClientRect();
        let width = ((containerRect.right - x - margin) / containerRect.width) * 100;

        if (width > 100) {
          width = 100;
        } else if (width < 5) {
          width = 5;
        }

        drawerRef.current.style.width = `${width}%`;
        setWidth(width);
      }
    },
    [dockPosition],
  );

  const style: React.CSSProperties = {};
  if (dockPosition === DockPosition.Right) {
    style.width = `${widthRef.current}%`;
  }

  if (dockPosition === DockPosition.Bottom) {
    style.height = `${heightRef.current}%`;
  }

  return (
    <div
      ref={drawerRef}
      key={dockPosition}
      data-position={dockPosition}
      data-testid="drawer"
      style={style}
      className={twMerge(
        // Custom class to override the some job list item styles (consolidate once this is the source of truth)
        "drawer group contain-layout",

        "relative z-30 rounded-md border border-gray-500 bg-white",
        classNames({
          // Centre-docked
          "absolute bottom-0 left-0 right-0 top-0 min-w-full":
            dockPosition === DockPosition.Center || dockPosition === DockPosition.Right,

          // Right-docked w/ falling back to center-docked
          "@xl-bbui:relative @xl-bbui:bottom-[initial] @xl-bbui:left-[initial] @xl-bbui:right-[initial] @xl-bbui:top-[initial] @xl-bbui:min-w-[560px] @xl-bbui:rounded-md @xl-bbui:border":
            dockPosition === DockPosition.Right,

          // Expanded
          "fixed bottom-0 left-0 right-0 top-0 z-50 border-none":
            dockPosition === DockPosition.Full,
        }),
      )}
    >
      <div className="hidden md:!block">
        {dockPosition === DockPosition.Bottom && (
          <ResizeHandle onResize={resize} direction="vertical" onDoubleClick={resetDrawerHeight} />
        )}

        {dockPosition === DockPosition.Right && (
          <ResizeHandle onResize={resize} direction="horizontal" onDoubleClick={resetDrawerWidth} />
        )}
      </div>

      <div className="flex h-full flex-col">
        <div className="top-0 z-10 flex gap-3 px-2.5 py-2">
          <ToolbarButton onClick={onClose} data-testid="drawer-close-button" title="Close">
            <Icon icon="heroicons/20/solid/x-mark" className="h-5" />
          </ToolbarButton>

          {actions}

          <div className="ml-auto hidden gap-1 md:!flex">
            <DockPositions onClose={onClose} />
          </div>
        </div>
        <div className="relative flex-auto">
          <div className="absolute left-0 top-0 h-full w-full overflow-hidden rounded-b-md">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const positions = [
  {
    icon: "custom/outline/panel-bottom",
    position: DockPosition.Bottom,
    title: "Dock bottom",
    className: "",
  },
  {
    icon: "custom/outline/panel-right",
    position: DockPosition.Right,
    title: "Dock right-hand side",
    className: "hidden @xl-bbui:!flex",
  },
  {
    icon: "custom/outline/panel",
    position: DockPosition.Center,
    title: "Dock center",
    className: "",
  },
  {
    icon: "custom/outline/maximize",
    position: DockPosition.Full,
    title: "Expand to full screen",
    className: "",
  },
];

/**
 * A collection of buttons to change the dock position of the drawer.
 *
 * Clicking a button will change the dock position and if the dock position is already selected, the drawer will close.
 */
const DockPositions = ({ onClose }: { onClose: () => void }) => {
  const track = useTrack();
  const dockPosition = useAdjustedDockPosition();
  const setDockPosition = useBuildPreferencesStore((state) => state.setDockPosition);

  const updateDockPosition = useCallback((position: DockPosition) => {
    track("Build Drawer Position Changed", { position });
    setDockPosition(position);
  }, []);

  return (
    <ToolbarButtonGroup data-position={dockPosition}>
      {positions.map(({ icon, position, title, className }) => (
        <ToolbarButton
          className={className}
          title={title}
          key={position}
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => {
            // Close the drawer if the active button is clicked again
            if (dockPosition === position) {
              onClose();
            } else {
              updateDockPosition(position);
            }
          }}
          isActive={dockPosition === position}
        >
          <Icon icon={icon} className="h-4" />
        </ToolbarButton>
      ))}
    </ToolbarButtonGroup>
  );
};
